/**
 * CELL-RENDER-FORMATTER-1 (s206) — the one answer to "given this stored cell, what text does a
 * person read?"
 *
 * ## Why this exists
 *
 * `generic_entries.value` is a single nullable text column for every field type in the product
 * (schema.ts:779), so it holds THREE different things: plain text, an address JSON object, and a
 * reference JSON object (`{ display, ref? }` — `institution` since s202, `contact` since s204).
 * Until this module, every render site picked its own formatter by hand, and three different
 * mechanisms were in use across ten files: a bare `formatReferenceSummary` (reference only, blind
 * to addresses), an `isReferenceFieldType` gate, and `renderIdentityValue` (all three, sniffed).
 *
 * Eleven leaks were found one at a time across five sessions, and three of those sessions each
 * believed they had swept the class — `letter-data.ts` was declared closed after two fixes with a
 * third unformatted read two hundred lines below. The census (s206) priced the alternatives; this
 * is the shared half of the ruling. The other half is `scripts/check-cell-render.ts`, which is what
 * actually obliges a new site to use this.
 *
 * ## The contract, and the one subtlety in it
 *
 * 🔴 **A DECLARED `fieldType` is AUTHORITATIVE — it is never second-guessed by looking at the
 * value.** Shape-sniffing a declared field is how a third shape gets silently mishandled later
 * (the rule applied to `planner-lens.ts:288` at s206), and it would actively corrupt a legitimate
 * `text` cell whose content happens to look like JSON. A caller that knows the type gets exactly
 * the transform that type declares, and nothing else.
 *
 * 🔴 **Sniffing happens ONLY when no type is available**, because some callers genuinely do not
 * have one — `server/record-identity.ts` composes labels from `{ id, fieldKey }` rows with no type
 * column. For them, returning raw JSON is strictly worse than recognising a known shape, so the
 * fallback exists and is deliberately the second-class path.
 *
 * ## What this module must NOT become
 *
 * It does not import `TEMPLATE_SPEC`. This file is reachable from client runtime code, and pulling
 * the spec in would put 534K / 14,748 lines into the browser bundle for a label (the D-199.18
 * constraint that `shared/constants/record-fields.ts` already lives under).
 *
 * It is also not the WIRE format. `entries-routes.ts` deliberately sends the raw cell to the
 * client, because `ContactInput` needs `ref` to keep a record link alive (`ContactInput.tsx:145`).
 * Formatting belongs at the point of RENDER, never at transport.
 */
import { formatAddressSummary, parseAddress } from "./address";
import { formatPlannerCurrency } from "./currency";
import { formatReferenceSummary, isReferenceFieldType, parseReference } from "./reference-value";

/** The `FieldType` whose stored value is an address object (`shared/template-spec/types.ts`). */
export const ADDRESS_FIELD_TYPE = "address";

/**
 * The `FieldType` whose stored value is a MONEY amount (`shared/template-spec/types.ts`).
 *
 * 🔴 s209. Unlike `address` and the reference types, a currency cell holds a PLAIN string — the
 * planner's `8000`. It is here not because the value needs parsing but because it needs
 * PRESENTING, and until s209 only the browser did that: `EditableCell` and `CurrencyFieldInput`
 * both call `formatPlannerCurrency`, so a planner saw `$8,000.00` while every document built on
 * the server printed `8000`. The `$` was client-render-only.
 *
 * 🔴 It is DECLARED, never sniffed. The sniffing fallback below deliberately does NOT recognise
 * money — nothing about the string `8000` says dollars rather than a year, a quantity or a
 * postal-adjacent number, and a `text` cell holding a bare number must keep rendering verbatim.
 * A cell formats as money because a field DECLARED it currency, which is the same rule this
 * module already applies to the two JSON shapes.
 */
export const CURRENCY_FIELD_TYPE = "currency";

/**
 * True when a field's DECLARED type means its stored value is structured JSON rather than text.
 *
 * One list, asked by every caller. A FOURTH structured shape is added here and in `renderCellText`
 * below — and nowhere else, which is the whole point of the module.
 */
export function isStructuredFieldType(fieldType: string | null | undefined): boolean {
  return isReferenceFieldType(fieldType) || fieldType === ADDRESS_FIELD_TYPE;
}

/**
 * The text a person should read for one stored cell.
 *
 * @param value     the raw `generic_entries.value`
 * @param fieldType the field's DECLARED type when the caller has it. Authoritative — pass it
 *                  whenever it is available, because it removes all guessing. Omit ONLY when the
 *                  caller genuinely has no type column, in which case the shape is recognised
 *                  instead.
 *
 * Empty in, empty out. A plain string always passes through unchanged, which is the G8 promise
 * every flipped field depends on: turning a field into a reference type must not change what an
 * existing value renders as.
 */
export function renderCellText(value: string | null | undefined, fieldType?: string | null): string {
  const v = (value ?? "").trim();
  if (!v) return "";

  if (fieldType != null && fieldType !== "") {
    if (isReferenceFieldType(fieldType)) return formatReferenceSummary(v);
    if (fieldType === ADDRESS_FIELD_TYPE) return formatAddressSummary(v);
    if (fieldType === CURRENCY_FIELD_TYPE) return formatPlannerCurrency(v);
    // Declared, and declared PLAIN. Returned verbatim on purpose — a `text` cell holding
    // JSON-shaped content is the planner's text, not a structure for us to reinterpret.
    return v;
  }

  return renderCellTextBySniff(v);
}

/**
 * The no-declaration fallback: recognise the shape.
 *
 * 🔴 Reference is checked BEFORE address, and the order is load-bearing (D-202.4). The address
 * join reads six address keys; handed a reference it finds none and returns "", so the value would
 * go BLANK rather than wrong — the harder failure to see. `parseAddress` is strict since s202 and
 * would reject a reference anyway, but the ordering does not lean on that: either function stays
 * correct if the other changes.
 */
function renderCellTextBySniff(trimmed: string): string {
  const ref = parseReference(trimmed);
  if (ref) return ref.display;
  if (parseAddress(trimmed)) return formatAddressSummary(trimmed);
  return trimmed;
}
